import {
  Component,
  ElementRef,
  ViewChild,
  OnInit,
  Renderer2,
  Input,
  ChangeDetectionStrategy,
  ChangeDetectorRef
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Observable, takeUntil, fromEvent, tap, catchError, of, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { DestroyService, AlertService } from '@selfai-platform/shared';
import { AgentService, ThreadManagementService, ChatService, LLMOrchestratorService } from '../../services';
import { ChatMessage, Agent, ChatThread } from '../../models';
import { LLMAppsResponse } from '../../types/service-interfaces';
import {
  MessageSentEvent,
  ThreadSelectedEvent,
  VisibilityChangeEvent,
  ErrorEvent,
  CodeEditorRequestEvent,
  ContextClearedEvent
} from '../../types';
import { Button } from 'primeng/button';
import { MessageModule } from 'primeng/message';
import { TooltipModule } from 'primeng/tooltip';
import { DialogService, provideDialogService } from '../../../dialog';
import { ChatContextViewerComponent } from '../chat-context-viewer';
import { ChatThreadsPanelComponent } from '../chat-threads-panel';
import { ChatMessagesListComponent } from '../chat-messages-list';
import { ChatMessageInputComponent } from '../chat-message-input';
import { CHAT_CONSTANTS } from '../../constants';

@Component({
  selector: 'selfai-platform-chat',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    Button,
    MessageModule,
    TooltipModule,
    ChatThreadsPanelComponent,
    ChatMessagesListComponent,
    ChatMessageInputComponent
  ],
  templateUrl: './chat.component.html',
  styleUrls: ['./chat.component.scss'],
  providers: [...provideDialogService(), DestroyService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatComponent implements OnInit {
  @Input() jsonDataInput = '';

  @ViewChild('chatContainer') private chatContainer!: ElementRef;

  public messages$: Observable<ChatMessage[]>;
  public isProcessing$: Observable<boolean>;
  public isProcessing = false;
  public isAgentHealthy = false;
  public isDragging = false;
  public agentError: string | null = null;
  public selectedAgent: Agent | null = null;
  public threads$: Observable<ChatThread[]>;
  public activeThread$: Observable<ChatThread | null>;
  public currentThread: ChatThread | null = null;
  public showThreads = true;
  public currentAppName$: Observable<string | null>;

  private startX = 0;
  private startWidth = 0;

  constructor(
    private readonly chatService: ChatService,
    private readonly agentService: AgentService,
    private readonly threadManagementService: ThreadManagementService,
    private readonly llmOrchestratorService: LLMOrchestratorService,
    private readonly renderer: Renderer2,
    private readonly dialogService: DialogService,
    private readonly cdr: ChangeDetectorRef,
    private readonly destroy$: DestroyService,
    private readonly translateService: TranslateService,
    private readonly alertService: AlertService,
  ) {
    this.messages$ = this.chatService.messages$;
    this.isProcessing$ = this.chatService.isProcessing$;
    this.threads$ = this.threadManagementService.threads$;
    this.activeThread$ = this.threadManagementService.activeThread$;

    this.agentService.agentHealth$.pipe(
      takeUntil(this.destroy$)
    ).subscribe((isHealthy) => {
      this.isAgentHealthy = isHealthy;
      this.agentError = isHealthy ? null : 'Agent backend is unavailable';
      this.cdr.markForCheck();
    });

    this.isProcessing$.pipe(takeUntil(this.destroy$)).subscribe((processing) => {
      this.isProcessing = processing;
      this.cdr.markForCheck();
    });

    this.chatService.selectedAgent$.pipe(
      takeUntil(this.destroy$)
    ).subscribe((agent) => {
      this.selectedAgent = agent;
      this.cdr.markForCheck();
    });

    this.activeThread$.pipe(
      takeUntil(this.destroy$)
    ).subscribe((thread) => {
      this.currentThread = thread;
      this.cdr.markForCheck();
    });

    this.currentAppName$ = combineLatest([
      this.activeThread$,
      this.llmOrchestratorService.availableApps$
    ]).pipe(
      map(([thread, availableApps]) => {
        if (!thread?.metadata?.app_id || !availableApps) {
          return this.selectedAgent?.name || null;
        }
        
        const pipelineApp = availableApps.pipeline?.[thread.metadata.app_id];
        const commonApp = availableApps.common?.[thread.metadata.app_id];
        const app = pipelineApp || commonApp;
        
        return app?.name || this.selectedAgent?.name || null;
      })
    );
  }

  public ngOnInit(): void {
    this.chatService.contextSubject
      .pipe(takeUntil(this.destroy$))
      .subscribe((context) => {
        this.jsonDataInput = context;
        this.cdr.detectChanges();
      });

    const savedWidth = localStorage.getItem(CHAT_CONSTANTS.WIDTH_KEY);
    if (savedWidth) {
      this.setContainerWidth(parseInt(savedWidth, 10));
    }

    this.checkAgentHealth();
    
    this.llmOrchestratorService.getApps()
      .pipe(
        catchError((error) => {
          this.alertService.error(this.translateService.instant('chat.errors.failed-to-load-apps') + ': ' + (error.message || error));
          return of(null);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  public startResize(event: MouseEvent): void {
    event.preventDefault();
    this.isDragging = true;
    this.startX = event.pageX;
    this.startWidth = this.chatContainer.nativeElement.offsetWidth;``

    const mouseMoveListener = fromEvent<MouseEvent>(document, 'mousemove')
      .pipe(takeUntil(fromEvent(document, 'mouseup')))
      .subscribe(e => this.onResize(e));

    fromEvent(document, 'mouseup')
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.isDragging = false;
        mouseMoveListener.unsubscribe();
      });
  }

  public onThreadsVisibilityChange(event: VisibilityChangeEvent): void {
    this.showThreads = event.isVisible;
  }

  public onThreadSelected(event: ThreadSelectedEvent): void {
    this.currentThread = event.thread;
    this.agentError = null;
  }

  public onThreadError(event: ErrorEvent): void {
    this.agentError = event.error;
  }

  public onMessageSent(event: MessageSentEvent): void {
    if (!this.selectedAgent) return;

    this.chatService.sendMessage(event.message, this.selectedAgent.type, event.jsonData)
      .pipe(
        catchError((error) => {
          this.alertService.error(this.translateService.instant('chat.errors.error-sending-message') + ': ' + (error.message || error));
          this.agentError = error.message || this.translateService.instant('chat.errors.failed-to-send-message');
          return of(null);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();

    this.agentError = null;
  }

  public onContextCleared(_event?: ContextClearedEvent): void {
    this.jsonDataInput = '';
    this.cdr.detectChanges();
  }

  public clearContext(): void {
    this.onContextCleared();
  }

  public onInputError(event: ErrorEvent): void {
    this.agentError = event.error;
  }



  private checkAgentHealth(): void {
    this.agentService.checkAgentHealth()
      .pipe(
        tap((isHealthy) => {
          this.isAgentHealthy = isHealthy;
          this.agentError = isHealthy ? null : this.translateService.instant('chat.errors.agent-backend-unavailable');
        }),
        catchError((error) => {
          this.alertService.error(this.translateService.instant('chat.errors.agent-health-check-error') + ': ' + (error.message || error));
          this.agentError = this.translateService.instant('chat.errors.agent-status-check-error');
          return of(false);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }


  private onResize(event: MouseEvent): void {
    if (this.isDragging) {
      const width = this.startWidth - (event.pageX - this.startX);
      this.setContainerWidth(width);
      this.saveWidth(width);
    }
  }

  private setContainerWidth(width: number): void {
    if (!this.chatContainer?.nativeElement) {
      return;
    }
    const clampedWidth = Math.min(Math.max(width, CHAT_CONSTANTS.MIN_WIDTH), CHAT_CONSTANTS.MAX_WIDTH);
    this.renderer.setStyle(this.chatContainer.nativeElement, 'width', `${clampedWidth}px`);
  }

  private saveWidth(width: number): void {
    const clampedWidth = Math.min(Math.max(width, CHAT_CONSTANTS.MIN_WIDTH), CHAT_CONSTANTS.MAX_WIDTH);
    localStorage.setItem(CHAT_CONSTANTS.WIDTH_KEY, clampedWidth.toString());
  }


  public onAgentSelected(agent: Agent): void {
    this.selectedAgent = agent;

    this.threadManagementService.loadThreadsForAgent(agent.type)
      .pipe(
        catchError((error) => {
          this.alertService.error(this.translateService.instant('chat.errors.error-loading-threads') + ': ' + (error.message || error));
          return of([]);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((threads) => {
        if (threads.length > 0 && !this.currentThread) {
          this.threadManagementService.setActiveThread(threads[0]);
        }
      });
  }


  public openCodeInEditor(event: CodeEditorRequestEvent): void {
    console.log('Opening code in editor:', event.code);
  }

  public viewContext(): void {
    this.dialogService.showDialog(ChatContextViewerComponent, {
      header: 'Context Data Viewer',
      width: '90vw',
      height: '80vh',
      maximizable: true,
      resizable: true,
      draggable: true,
      modal: true,
      closable: true,
      closeOnEscape: true
    }).subscribe();
  }
}
